import { Print } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { displayCellType } from "Backend";
import { Logo } from "components/Icons";
import PCheckbox from "components/PCheckbox";
import { PortalContext } from "contexts/PortalContext";
import usePrintPage from "helpers/hooks/usePrintPage";
import { useLensSpecsLoader } from "loaders/lens-specification.loader";
import { useContext, useState } from "react";
import { createPortal } from "react-dom";
import { useAppSelector } from "redux/hooks";
import { selectClientLens } from "redux/slices/clientLens";
import { selectForm } from "redux/slices/form.slice";
import { selectClient } from "redux/slices/session";
import theme from "theme";
import { P } from "theme/typography";

const PAGE_BREAK = "page-break";

const LensSpecification = () => {
  const { portalRefElement } = useContext(PortalContext);
  const form = useAppSelector(selectForm);
  const lens = useAppSelector(selectClientLens);
  const client = useAppSelector(selectClient);
  const [enableCriteriaMode, setEnableCriteriaMode] = useState(false);
  const lensRuleStructure = useLensSpecsLoader();

  usePrintPage(`${lens.name}, ${client?.name} - Specification`);

  // Filter out the hidden pages & pages without sections
  const pagesWithSections = form?.pages.filter((page) => {
    return form.sections.some((section) => section.formPageId === page.id) && !page.isHidden;
  });

  // Filter lensRuleStructure to only include fields with subtypes
  const lensRuleStructureWithSubtypes = lensRuleStructure.filter((rule) => rule.subtypes.length > 0);

  return (
    <>
      {portalRefElement &&
        createPortal(
          <Box
            sx={{
              display: "flex",
              gap: 1,
            }}>
            <PCheckbox
              disabled={lensRuleStructure.length === 0}
              label="Show Criteria"
              checked={enableCriteriaMode}
              onChange={(e) => setEnableCriteriaMode(e.target.checked)}
            />
            <Button variant="contained" onClick={() => window.print()}>
              <Print />
            </Button>
          </Box>,
          portalRefElement
        )}
      <Card
        sx={{
          flex: 1,
          p: 8,
          overflow: "auto",
        }}>
        <CardContent
          id="printableArea"
          sx={{
            flex: 1,
            m: 4,
            mt: 0,
            marginBottom: 8,
          }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}>
            <Logo size={64} />
            <Box
              sx={{
                mt: 4,
                display: "flex",
                flexDirection: "column",
              }}>
              <P variant="xl2Bold">{lens.name}</P>
              {client?.name !== undefined && <P variant="lg">{client.name}</P>}
              <P variant="sm" sx={{ mt: 2 }}>
                The information below is based on the litigation’s case criteria and will be used to build the
                litigation’s mining algorithms (“lens”). An exhaustive list of key terms related to the litigation’s
                injuries and treatments is not required to build the lens. Pattern will use its extensive health and
                biomedical ontologies and resources to develop the mining criteria for each category. Still, you may
                have additional specialized knowledge of the litigation and claimant populations that should be
                incorporated into the mining processes. The objective will be to categorize and guide the firms’
                reviewers through the results efficiently and with consistency. In addition, we have indicated the data
                points that are most important to the case and that will require validation and additional data captured
                by the MD reviewer, attorney, paralegal, or case manager.{" "}
              </P>
              {pagesWithSections !== undefined &&
                pagesWithSections.map((page, idx) => (
                  <Box sx={{ mt: 4 }} key={idx}>
                    <P variant="xl">{page.name}</P>
                    {form?.sections
                      .filter((section) => section.formPageId === page.id)
                      .map((section, index) => (
                        <Box key={index} sx={{ pt: 4 }} className={PAGE_BREAK}>
                          <Box sx={{ pb: 0.5 }}>
                            <P variant="xl">
                              {index + 1}. {section.name}
                            </P>
                          </Box>
                          <Divider />
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "1fr",
                              gap: 2,
                            }}>
                            {form.fields
                              .filter((field) => field.sectionId === section.id)
                              .map((field, index) => (
                                <>
                                  <Box
                                    key={index}
                                    sx={{ mt: 2, display: "flex", flexDirection: "column" }}
                                    className={PAGE_BREAK}>
                                    <Box>
                                      <P variant="lgBold">{field.outlinePrefix} - </P>
                                      <P variant="lgBold">{field.name}</P>
                                    </Box>
                                    {field.prompt && <P variant="base">{field.prompt}</P>}

                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        mb: 0.5,
                                      }}>
                                      <P variant="baseBold">Response Format</P>
                                      {field.promptYesNo && <P variant="base">Prompt Yes/No </P>}
                                      <P variant="base">{field.repeating ? "Multi Response" : "Single Response"}</P>
                                    </Box>
                                    <P variant="baseBold">Data Points</P>
                                    <Table>
                                      <TableHead>
                                        <TableRow>
                                          <TableCell sx={{ px: 0 }}>Name</TableCell>
                                          <TableCell sx={{ whiteSpace: "nowrap" }}>Data Type</TableCell>
                                          <TableCell sx={{ whiteSpace: "nowrap" }}>Required</TableCell>
                                          <TableCell sx={{ whiteSpace: "nowrap" }}>Available Options</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {field.cells.map((cell, index) => (
                                          <TableRow
                                            key={index}
                                            sx={{
                                              verticalAlign: "top",
                                            }}>
                                            <TableCell sx={{ pl: 0, whiteSpace: "nowrap" }}>{cell.name}</TableCell>
                                            <TableCell>{displayCellType(cell.type)}</TableCell>
                                            <TableCell>{cell.optional ? "Optional" : "Required"}</TableCell>
                                            <TableCell>
                                              {cell.selectOptions?.length ? `${cell.selectOptions.join(", ")}` : ""}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </Box>
                                  {enableCriteriaMode &&
                                    lensRuleStructureWithSubtypes.filter((rule) => rule.field.id === field.id).length >
                                      0 && (
                                      <Box
                                        className={PAGE_BREAK}
                                        sx={{
                                          mt: 3,
                                          p: 1,
                                          px: 2,
                                          border: `1px solid ${theme.palette.grey[200]}`,
                                          position: "relative",
                                        }}>
                                        <P
                                          variant="base"
                                          sx={{
                                            backgroundColor: theme.palette.background.paper,
                                            position: "absolute",
                                            left: 10,
                                            top: -14,
                                            display: "block",
                                            px: 0.75,
                                          }}>
                                          Criteria
                                        </P>
                                        {lensRuleStructure
                                          .filter((rule) => rule.field.id === field.id)
                                          .map((rule, index) => (
                                            <Grid container key={index} sx={{ py: 1 }} spacing={3}>
                                              {rule.subtypes.map((subtype, index) => (
                                                <Grid item xs={6} key={index}>
                                                  <P variant="smBold">{subtype.subtype}</P>
                                                  <Box
                                                    sx={{
                                                      display: "flex",
                                                      flexDirection: "column",
                                                    }}>
                                                    <P variant="sm" key={index}>
                                                      {subtype.descriptions.map((desc) => desc.description).join(", ")}
                                                    </P>
                                                  </Box>
                                                </Grid>
                                              ))}
                                            </Grid>
                                          ))}
                                      </Box>
                                    )}
                                </>
                              ))}
                          </Box>
                        </Box>
                      ))}
                  </Box>
                ))}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </>
  );
};

export default LensSpecification;
